
import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import Tag from '../../components/base/Tag';
import SearchBar from '../../components/base/SearchBar';

interface ForumPost {
  id: number;
  title: string;
  content: string;
  author_name: string;
  category: string;
  created_at: string;
  updated_at: string;
  replies_count: number;
  is_pinned: boolean;
}

export default function Forum() {
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('すべて');
  const [showNewPostForm, setShowNewPostForm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    author_name: '匿名',
    category: '情報提供'
  });

  const categories = ['すべて', '情報提供', 'リンク記載希望', 'ご意見', '雑談', 'その他'];

  // SEO設定
  useEffect(() => {
    document.title = "匿名掲示板 - 兵庫県政検証サイト";
    
    // メタタグの設定
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.setAttribute('name', 'description');
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.setAttribute('content', '誰でも匿名で自由に議論に参加できる掲示板です。建設的な議論を心がけ、情報提供や意見交換を行えます。');
    
    // Schema.org JSON-LD for Forum Page
    const forumPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "匿名掲示板",
      "description": "誰でも匿名で自由に議論に参加できる掲示板",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/forum`,
      "inLanguage": "ja",
      "isPartOf": {
        "@type": "WebSite",
        "name": "兵庫県政検証サイト",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
      },
      "mainEntity": {
        "@type": "DiscussionForumPosting",
        "name": "県政に関する議論フォーラム",
        "description": "匿名での情報交換と議論の場"
      }
    };
    
    let existingScript = document.querySelector('script[data-schema="forum"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'forum');
    script.textContent = JSON.stringify(forumPageSchema);
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.querySelector('script[data-schema="forum"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  // URLパラメータからカテゴリを取得してフィルタを設定
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam && categories.includes(categoryParam)) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/forum-api`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        console.error('Response status:', response.status);
        console.error('Response statusText:', response.statusText);
        throw new Error(`Failed to fetch posts: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      setPosts(data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      // エラーが発生した場合は空の配列を設定
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPost = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPost.title.trim() || !newPost.content.trim()) {
      alert('タイトルと内容を入力してください');
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/forum-api/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPost)
      });

      if (!response.ok) {
        const error = await response.json();
        alert(error.error || '投稿に失敗しました');
        return;
      }

      setNewPost({ title: '', content: '', author_name: '匿名', category: '情報提供' });
      setShowNewPostForm(false);
      fetchPosts();
    } catch (error) {
      console.error('Error creating post:', error);
      alert('投稿に失敗しました');
    }
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    
    // URLパラメータを更新
    if (category === 'すべて') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = searchTerm === '' || 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'すべて' || post.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">読み込み中...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* ヘッダー */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            匿名掲示板
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            誰でも匿名で自由に議論に参加できる掲示板です。建設的な議論を心がけましょう。
          </p>
        </div>

        {/* 検索とフィルター */}
        <div className="mb-6 space-y-4">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="投稿を検索..."
          />
          
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'primary' : 'outline'}
                size="sm"
                onClick={() => handleCategoryChange(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* 新規投稿ボタン */}
        <div className="mb-6">
          <Button 
            onClick={() => setShowNewPostForm(!showNewPostForm)}
            className="whitespace-nowrap"
          >
            <i className="ri-add-line mr-2"></i>
            新規投稿
          </Button>
        </div>

        {/* 新規投稿フォーム */}
        {showNewPostForm && (
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              新規投稿
            </h2>
            <form onSubmit={handleSubmitPost} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  投稿者名
                </label>
                <input
                  type="text"
                  value={newPost.author_name}
                  onChange={e => setNewPost(prev => ({ ...prev, author_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="匿名"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  カテゴリー
                </label>
                <select
                  value={newPost.category}
                  onChange={e => setNewPost(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm pr-8"
                >
                  {categories.slice(1).map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  タイトル *
                </label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={e => setNewPost(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="投稿のタイトルを入力してください"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  内容 *
                </label>
                <textarea
                  value={newPost.content}
                  onChange={e => setNewPost(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                  rows={6}
                  placeholder="投稿内容を入力してください..."
                  maxLength={500}
                  required
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {newPost.content.length}/500文字
                </p>
              </div>
              
              <div className="flex space-x-3">
                <Button type="submit" className="whitespace-nowrap">
                  投稿する
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => setShowNewPostForm(false)}
                  className="whitespace-nowrap"
                >
                  キャンセル
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* 投稿一覧 */}
        <div className="space-y-4">
          {filteredPosts.length === 0 ? (
            <Card className="p-8 text-center">
              <i className="ri-message-3-line text-4xl text-gray-400 dark:text-gray-600 mb-4"></i>
              <p className="text-gray-600 dark:text-gray-400">
                {searchTerm || selectedCategory !== 'すべて' 
                  ? '条件に一致する投稿が見つかりません' 
                  : 'まだ投稿がありません。最初の投稿をしてみませんか？'}
              </p>
            </Card>
          ) : (
            filteredPosts.map(post => (
              <Card key={post.id} className="p-6 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-3">
                      <Tag variant="category">{post.category}</Tag>
                      {post.is_pinned && (
                        <div className="flex items-center justify-center w-5 h-5">
                          <i className="ri-pushpin-fill text-yellow-500"></i>
                        </div>
                      )}
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ID: {post.id}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      <Link 
                        to={`/forum/${post.id}`}
                        className="hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer"
                      >
                        {post.title}
                      </Link>
                    </h3>
                    
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">
                      {post.content}
                    </p>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-4">
                        <span>投稿者: {post.author_name}</span>
                        <div className="flex items-center space-x-1">
                          <div className="flex items-center justify-center w-4 h-4">
                            <i className="ri-message-2-line"></i>
                          </div>
                          <span>{post.replies_count} 返信</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span>作成: {new Date(post.created_at).toLocaleDateString('ja-JP')}</span>
                        {post.updated_at !== post.created_at && (
                          <span>更新: {new Date(post.updated_at).toLocaleDateString('ja-JP')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* 利用規約 */}
        <Card className="p-6 mt-8 bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-5 h-5">
                <i className="ri-information-line text-gray-600 dark:text-gray-400"></i>
              </div>
              <span>利用上の注意</span>
            </div>
          </h3>
          <ul className="text-sm text-gray-700 dark:text-gray-400 space-y-2">
            <li>• 誹謗中傷、差別的発言、攻撃的な内容は禁止されています</li>
            <li>• 個人情報や機密情報の投稿は避けてください</li>
            <li>• 建設的で健全な議論を心がけましょう</li>
            <li>• 不適切な投稿は管理者により削除される場合があります</li>
            <li>• NGワードが含まれる投稿は自動的に投稿が拒否されます</li>
          </ul>
        </Card>

      </div>

      <Footer />
    </div>
  );
}
